import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
} from "@mui/material";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import { Formik, Form, FieldArray, Field } from "formik";
import { Box } from "@mui/system";
import { useApplyRegularizationMutation } from "@/api/services/attendance/regularization/EmployeeRegularization";
import { format, parse, isValid } from "date-fns";
import { StyledButton } from "@/components";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { toast } from "@/components/toastify";

const AppliedDatesList = ({
  appliedDates,
  managers,
  attendanceTime,
  isCardOpen,
  handleButtonClick,
  handleDelete,
  attendanceArray,
  getLockMonth,
  restrictedLeaveDates,
}) => {
  const localStorageData = JSON.parse(localStorage.getItem("loginDetails"));
  const employeeDetails = localStorageData?.userDetails || {};
  const orgCode = employeeDetails?.activeOrganization?.organizationCode || "";

  // useApplyRegularizationMutation
  const [applyRegularization] = useApplyRegularizationMutation();

  // const managerId = managers.length > 0 ? managers[0].id : "";
  const [defaultManagerId, setDefaultManagerId] = useState("");
  useEffect(() => {
    if (managers.length > 0) {
      setDefaultManagerId(managers[0].id);
    }
  }, [managers]);

  let formatted = "";
  if (getLockMonth) {
    const parsedDate = parse(getLockMonth, "MM-yyyy", new Date());

    if (isValid(parsedDate)) {
      formatted = format(parsedDate, "MM-yyyy");
    } else {
      console.error("Invalid date format:", getLockMonth);
    }
  }

  const getAttendanceRecord = (date) =>
    attendanceTime.find((item) => item.attendanceDate === date);

  const handleApplyRegularization = async (values) => {

    const sortSessions = (sessions) => {
      const order = ["Session 1", "Session 2"];
      return sessions.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    };
    const regularizationInfo = values.appliedDates.map((date) => {
      const attendanceEntry = attendanceArray.find(
        (att) => att.attendanceDate === date.applydatearr
      );

      let sessions = date.sessions || [];
      if (typeof sessions === "string") {
          sessions = sessions.split("-").map((s) => s.trim());
        }
        sessions = sortSessions([...sessions]);
      const restrictedSessions = date?.restrictedSessions || [];

      let inTime = "";
      let outTime = "";

      // Normal logic first
      if (sessions.length === 1 && sessions.includes("Session 1")) {
        inTime = attendanceEntry[0]?.inTime || attendanceEntry?.inTime;
        outTime = attendanceEntry[0]?.halfDay || attendanceEntry?.halfDay;
      } else if (sessions.length === 1 && sessions.includes("Session 2")) {
        inTime = attendanceEntry[0]?.halfDay || attendanceEntry?.halfDay;
        outTime = attendanceEntry[0]?.outTime || attendanceEntry?.outTime;
      } else {
        inTime = attendanceEntry[0]?.inTime || attendanceEntry?.inTime;
        outTime = attendanceEntry[0]?.outTime || attendanceEntry?.outTime;
      }

      // Override with restriction logic (if any)
      if (restrictedSessions.length > 0) {
        if (
          restrictedSessions.length === 2 ||
          (restrictedSessions.includes("Session 1") &&
            restrictedSessions.includes("Session 2"))
        ) {
          inTime = attendanceEntry[0]?.inTime || attendanceEntry?.inTime;
          outTime = attendanceEntry[0]?.halfDay || attendanceEntry?.halfDay;
        } else if (restrictedSessions.includes("Session 1")) {
          inTime = attendanceEntry[0]?.halfDay || attendanceEntry?.halfDay;
          outTime = attendanceEntry[0]?.outTime || attendanceEntry?.outTime;
        } else if (restrictedSessions.includes("Session 2")) {
          inTime = attendanceEntry[0]?.inTime || attendanceEntry?.inTime;
          outTime = attendanceEntry[0]?.halfDay || attendanceEntry?.halfDay;
        }
      }

      const sessionStr = sessions.length > 0 ? sessions.join(" - ") : "";

      return {
        date: date.applydatearr,
        reason: date.reason,
        inTime,
        outTime,
        session: sessionStr,
        key: date.workType,
      };
    });

    const requestData = {
      regularizationInfo,
      appliedTo: values.applieTo,
      remark: values.attRemarks,
      monthYear: formatted,
    };
    // console.log(requestData);

    try {
      await applyRegularization(requestData).unwrap();
      toast("success", "Regularization applied successfully!");
      handleButtonClick();
    } catch (error) {
      // console.error("Regularization API Error:", error);
    }
  };

  return (
    <Formik
      initialValues={{
        appliedDates: Array.isArray(appliedDates)
          ? appliedDates.map((date, index) => {
              const matchingAttendance = attendanceArray.find(
                (att) => att.date === date.date
              );

              const restrictedSessions =
                restrictedLeaveDates?.[date.applydatearr] || [];

              return {
                applydatearr: date.applydatearr || "",
                date: date.date,
                month: date.month,
                inTime:
                  matchingAttendance?.inTime ||
                  attendanceTime[index]?.inTime ||
                  "",
                outTime:
                  matchingAttendance?.outTime ||
                  attendanceTime[index]?.outTime ||
                  "",
                reason: date.reason || "",
                id: date.id || "",
                workType: date.workType || "REG",
                sessions: date.sessions || ["Session 1", "Session 2"],
                restrictedSessions,
              };
            })
          : [],
        applieTo: defaultManagerId || "",
        attRemarks: "",
      }}
      onSubmit={handleApplyRegularization}
      enableReinitialize
    >
      {({ values, setFieldValue, handleChange, isSubmitting }) => (
        <Form>
          <FieldArray name="appliedDates">
            {({ remove }) => (
              <>
                {values.appliedDates.map((appliedDate, index) => (
                  <Card
                    key={`${appliedDate.applydatearr}_${index}`}
                    sx={{ mb: 2, p: 2, boxShadow: "1px 0px 8px 0px #E9E9E9" }}
                  >
                    <Grid container spacing={2} alignItems="stretch">
                      {/* Date and Month */}
                      <Grid
                        item
                        xs={12}
                        md={2}
                        sx={{
                          textAlign: "center",
                          padding: "8px 0",
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{
                            textAlign: "center",
                            borderRight: "1px solid #ccc",
                            padding: "8px 0",
                          }}
                        >
                          {appliedDate.date} <br />
                          {appliedDate.month}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <Grid container spacing={1}>
                          {/* In Time */}
                          <Grid item xs={3}>
                            <Typography variant="body1" align="left">
                              In Time
                            </Typography>
                            {getAttendanceRecord(appliedDate.applydatearr)
                              ?.actualInTime || "--"}
                          </Grid>

                          {/* Out Time */}
                          <Grid item xs={3}>
                            <Typography variant="body1" align="left">
                              Out Time
                            </Typography>
                            {getAttendanceRecord(appliedDate.applydatearr)
                              ?.actualOutTime || "--"}
                          </Grid>

                          {/* work type */}
                          <Grid item xs={6}>
                            <FormikSelectInput
                              name={`appliedDates[${index}].workType`}
                              label="Work Type"
                              options={[
                                { value: "REG", label: "Regularization" },
                                ...(orgCode !== "ORG00002"
                                  ? [{ value: "WFH", label: "Work From Home" }]
                                  : []),
                                { value: "OD", label: "On Duty" },
                              ]}
                              value={
                                values.appliedDates[index]?.workType || "REG"
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                setFieldValue(
                                  `appliedDates[${index}].workType`,
                                  value
                                );
                              }}
                            />
                          </Grid>

                          {/* Sessions */}
                          <Grid
                            item
                            xs={12}
                            mt={2}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Typography variant="body1" align="left">
                              Sessions
                            </Typography>

                            {/* <Field name={`appliedDates[${index}].sessions`}>
                              {({ field, form }) => {
                                const sessionOptions = ["Session 1", "Session 2"];
                                const restrictedSessions = appliedDate.restrictedSessions || [];
                                const currentValue = field.value || [];

                                // On mount, remove restricted and ensure only allowed sessions are checked
                                useEffect(() => {
                                  const allowedSessions = sessionOptions.filter(
                                    (s) => !restrictedSessions.includes(s)
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].sessions`,
                                    allowedSessions
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].isRequired`,
                                    allowedSessions.length === 0
                                  );
                                }, [restrictedSessions.join(",")]); // Only runs when restriction changes

                                const handleCheckboxChange = (session) => {
                                  if (restrictedSessions.includes(session)) return; // prevent toggle

                                  const updated = currentValue.includes(session)
                                    ? currentValue.filter((val) => val !== session)
                                    : [...currentValue, session];

                                  form.setFieldValue(`appliedDates[${index}].sessions`, updated);
                                  form.setFieldValue(
                                    `appliedDates[${index}].isRequired`,
                                    updated.length === 0
                                  );
                                };

                                return (
                                  <Box display="flex" alignItems="center" gap={2} ml={2}>
                                    {sessionOptions.map((session) => {
                                      const isDisabled = restrictedSessions.includes(session);
                                      return (
                                        <Box key={session} display="flex" alignItems="center">
                                          <Checkbox
                                            size="small"
                                            checked={!isDisabled && currentValue.includes(session)}
                                            disabled={isDisabled}
                                            onChange={() => handleCheckboxChange(session)}
                                          />
                                          <Typography variant="body2" color={isDisabled ? "text.disabled" : "text.primary"} >{session}</Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              }}
                            </Field> */}

                            <Field name={`appliedDates[${index}].sessions`}>
                              {({ field, form }) => {
                                const sessionOptions = [
                                  "Session 1",
                                  "Session 2",
                                ];
                                const restrictedSessions =
                                  appliedDate.restrictedSessions || [];
                                const currentValue = field.value || [];

                                const [confirmOpen, setConfirmOpen] =
                                  useState(false);
                                const [pendingSession, setPendingSession] =
                                  useState(null);

                                // On mount → keep only allowed sessions
                                useEffect(() => {
                                  const allowedSessions = sessionOptions.filter(
                                    (s) => !restrictedSessions.includes(s)
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].sessions`,
                                    allowedSessions
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].isRequired`,
                                    allowedSessions.length === 0
                                  );
                                }, [restrictedSessions.join(",")]);

                                const handleCheckboxChange = (session) => {
                                  if (restrictedSessions.includes(session))
                                    return;

                                  if (currentValue.includes(session)) {
                                    // 🔹 Instead of unchecking directly, ask confirmation
                                    setPendingSession(session);
                                    setConfirmOpen(true);
                                  } else {
                                    // Add session normally
                                    const updated = [...currentValue, session];
                                    form.setFieldValue(
                                      `appliedDates[${index}].sessions`,
                                      updated
                                    );
                                    form.setFieldValue(
                                      `appliedDates[${index}].isRequired`,
                                      updated.length === 0
                                    );
                                  }
                                };

                                const handleConfirm = () => {
                                  const updated = currentValue.filter(
                                    (val) => val !== pendingSession
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].sessions`,
                                    updated
                                  );
                                  form.setFieldValue(
                                    `appliedDates[${index}].isRequired`,
                                    updated.length === 0
                                  );
                                  setConfirmOpen(false);
                                  setPendingSession(null);
                                };

                                const handleCancel = () => {
                                  setConfirmOpen(false);
                                  setPendingSession(null);
                                };

                                return (
                                  <>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={2}
                                      ml={2}
                                    >
                                      {sessionOptions.map((session) => {
                                        const isDisabled =
                                          restrictedSessions.includes(session);
                                        return (
                                          <Box
                                            key={session}
                                            display="flex"
                                            alignItems="center"
                                          >
                                            <Checkbox
                                              size="small"
                                              checked={
                                                !isDisabled &&
                                                currentValue.includes(session)
                                              }
                                              disabled={isDisabled}
                                              onChange={() =>
                                                handleCheckboxChange(session)
                                              }
                                            />
                                            <Typography
                                              variant="body2"
                                              color={
                                                isDisabled
                                                  ? "text.disabled"
                                                  : "text.primary"
                                              }
                                            >
                                              {session}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>

                                    {/* 🔹 Confirmation Dialog */}
                                    <Dialog
                                      open={confirmOpen}
                                      onClose={handleCancel}
                                    >
                                      <DialogTitle>Confirm Session</DialogTitle>
                                      <DialogContent>
                                        Are you sure you want to apply leave for{" "}
                                        <strong>{pendingSession}</strong> on
                                        this date?
                                        <br />
                                        If not, it will be considered an
                                        absence.
                                      </DialogContent>
                                      <DialogActions>
                                        <Button
                                          onClick={handleCancel}
                                          color="inherit"
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          onClick={handleConfirm}
                                          color="error"
                                          variant="contained"
                                        >
                                          Confirm
                                        </Button>
                                      </DialogActions>
                                    </Dialog>
                                  </>
                                );
                              }}
                            </Field>
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={5}>
                        <Grid container spacing={1}>
                          {/* Reason */}
                          <Grid item xs={8}>
                            <Typography variant="body1" align="left">
                              Reason
                            </Typography>
                            <Field
                              as={TextField}
                              size="small"
                              name={`appliedDates[${index}].reason`}
                              placeholder="Reason"
                              id={`reason_${index}`}
                              fullWidth
                              required
                            />
                          </Grid>

                          {/* Action */}
                          <Grid item xs={4} mt={3}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                              }}
                            >
                              <IconButton
                                color="inherit"
                                // onClick={() => handleDelete(appliedDate.id, appliedDate.applydatearr, index, remove)}
                                onClick={() => remove(index)}
                                aria-label="delete"
                                size="small"
                              >
                                <DeleteOutlined />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </>
            )}
          </FieldArray>

          {values.appliedDates.length >= 1 && (
            <div className="att-reg-submit-div">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <InputLabel
                    htmlFor="appliedTo"
                    required
                    sx={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    Applying to
                  </InputLabel>
                  <Select
                    name="applieTo"
                    // value={values.applieTo || defaultManagerId}
                    value={values.applieTo}
                    onChange={(event) => {
                      setFieldValue("applieTo", event.target.value);
                    }}
                    // value={selectedManager} onChange={handleAppliedToChange}
                    fullWidth
                    id="appliedTo"
                    required
                  >
                    <MenuItem value="" disabled>
                      Select Applied To
                    </MenuItem>
                    {managers.map((manager) => (
                      <MenuItem key={manager.id} value={manager.id}>
                        {manager.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <InputLabel
                    htmlFor="remarks"
                    sx={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    Remarks
                  </InputLabel>
                  <Field
                    as={TextField}
                    name="attRemarks"
                    id="remarks"
                    multiline
                    rows={4}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                }}
              >
                {isCardOpen && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleButtonClick}
                    sx={{ mr: 1, p: (theme) => theme.spacing(1, 5) }}
                  >
                    Clear
                  </Button>
                )}

                <StyledButton
                  type="submit"
                  label={
                    isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit"
                    )
                  }
                  variant="contained"
                  disabled={!isCardOpen}
                />
              </Box>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default AppliedDatesList;
